import { observable } from 'mobx';
import { cast, types } from 'mobx-state-tree';
import { IEntityStore } from '../main/entities';
import { mg } from '../mg/mg';
import { windowSvc } from '../services/WindowSvc';
import { EntityStore } from './EntityStore';
import { getOmnistore, IOmnistore } from './Omnistore';

type IStoreOrId = undefined | string | { id: string }
type IListFn = () => Array<IStoreOrId>

const toListFnMap = new WeakMap<{}, IListFn>();

export const EntityManagerStore = types
	.model('EntityManagerStore', {
		_lastAccess: 0,
		_lastRefresh: types.map(types.number),
		_isFailed: types.map(types.boolean),
		_refreshers: types.map(types.frozen()),
		_queue: types.array(types.string),
		_map: types.map(EntityStore),
	})
	.views(self => ({
		get _omnistore() : IOmnistore {
			return getOmnistore();
		}
	}))
	.views(self => ({
		get authStore() {
			return self._omnistore.authStore;
		},
		get reoctoStore() {
			return self._omnistore.reoctoStore;
		},
	}))
	.volatile(self => ({
		_volatileMap: observable.map<string, IEntityStore>({})
	}))
	.actions(self => ({
		_fixMap() {
			for (const e of self._volatileMap.entries()) self._map.set(e[0], e[1]);
			self._volatileMap.clear();
		},
	}))
	.views(self => ({
		byStoreId(storeId: string): IEntityStore {
			{
				const r = self._map.get(storeId);
				if (r) return r;
			}
			{
				const r = self._volatileMap.get(storeId);
				if (r) return r;
			}
			//console.log('byStoreId', storeId)
			mg.checkTrue(!storeId.match(/Item$/), 'Invalid storeId', storeId);
			const r = EntityStore.create({ id: storeId });
			self._volatileMap.set(storeId, r);
			setTimeout(() => self._fixMap); //TODO
			return r;
		},
		_values(): Array<IEntityStore> {
			return [...self._map.values()];
		},
	}))
	.views(self => ({
		get(name: string, masterId?: number): IEntityStore {
			return self.byStoreId(masterId === undefined ? name : name + ':' + masterId);
		},
	}))
	.actions(self => ({
		clear() {
			self._lastRefresh.clear();
			self._isFailed.clear();
			// self._refreshers.clear() //TODO
			self._queue.clear();
			for (let s of self._map.values()) s.clear();
		},

	}))
	.views(self => ({
		_todo() {
			const m = {};
			for (let key of self._refreshers.values()) {
				const listFn = toListFnMap.get(key);
				if (!mg.assertPresent(listFn, 'no listFn', key)) continue;
				if (!mg.assertTrue(typeof listFn === 'function', 'no listFn', key)) continue;
				for (let x of listFn()) {
					if (!x) continue;
					const storeId = typeof x === 'string' ? x : x.id;
					m[storeId] = 1;
				}
			}
			// console.log('_todo', Object.keys(m))
			return Object.keys(m);
		},
	}))
	.actions(self => ({
		_onFetch(key: string, isFailed: boolean) {
			//console.log('_onFetch', key, isFailed)
			const now = windowSvc.currentTimeMillis();
			self._isFailed.set(key, isFailed);
			self._lastRefresh.set(key, now);
		},
		_onAnyAccess() {
			self._lastAccess = windowSvc.currentTimeMillis();
		},
		_renewQueue() {
			self._queue = cast(self._todo());
		},
	}))
	.actions(self => ({
		step() {
			const reoctoStore = self.reoctoStore;
			// console.log(reoctoStore.isIdle)
			if (reoctoStore.isIdle) return;
			if (!self.authStore.authorization) return;
			if (!self._queue.length) setTimeout(() => self._renewQueue());
			const now = windowSvc.currentTimeMillis();
			if (reoctoStore.isOffline && now - self._lastAccess < 1000 * reoctoStore.offlineRefreshPeriod) return;
			const fastMillis = 1000 * reoctoStore.refreshPeriod;
			const slowMillis = reoctoStore.slowRefreshRatio * fastMillis;
			const failureMillis = 1000;
			// console.log('_doRefresh', self._queue.length)
			while (self._queue.length) {
				const k = self._queue.shift();
				if (!k) throw new Error('impossible');
				const store = self.byStoreId(k);
				const x = self._lastRefresh.get(k) || 0;
				const isSlow = !k.match(/^TemplateItem:|^AdCountdown/);
				const millis = self._isFailed.get(k) ? failureMillis : isSlow ? slowMillis : fastMillis;
				if (x && now - x < millis) continue;
				//console.log(k, isSlow, millis, now - x, '::', x - (now - millis))
				store.fetch()
					.then(() => self._onFetch(k, !store.etag))
					.finally(self._onAnyAccess);
				if (x) break;
			}
		},
	}))
	.actions(self => ({
		subscribe(subscriberId: string, listFn: IListFn) {
			const key = {};
			toListFnMap.set(key, listFn);
			self._refreshers.set(subscriberId, key);
			self.step();
		},
		unsubscribe(subscriberId: string) {
			self._refreshers.delete(subscriberId);
		},
	}));
